import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "axios";
import {IMultiTenant, PartialRecord, ReactQueryKey} from "../../types";

interface IGetTotalToBePaidParams {
    pickupDate: string,
    dropOffDate: string,
    rateDetailIds: number[],
    ancillaryDetailIds: {
        ancillaryCode: string,
        units: number | null,
        mileage: number | null
    }[],
    promo: {
        code: string,
        type: string
    }[],
    offerId: string,
    discountOverpriceSelected?: number,
    affiliationCode: string | null,
    tenant: string,
    dealer: string,
    language:string
}

type RateAmount = {
    rate: number,
    taxCode: string,
    currency: string
}

type TotalAmount = {
    total: number,
    currency: string
}

type ExtraKmAmount = {
    amount: number,
    currency: string
}

export interface IGetTotalToBePaidReturn {
    rate: PartialRecord<string, [RateAmount, RateAmount]>
    ancillariesTotal: {
        currency: string,
        amount: PartialRecord<string, [number, number]>,
        list: {
            code: string,
            label: string,
            amount: PartialRecord<string, [number, number]>,
            currency: string
        }[]
    } | null,
    promo: PartialRecord<string, number> | null,
    totalToBePaid: PartialRecord<string, [TotalAmount, TotalAmount]>,
    offerId: string,
    excessesTotal: {
        code: string,
        label: string,
        taxCode: string,
        currency: string,
        amount: PartialRecord<string, [number, number]>
    }[],
    totalToBePaidTaxExcluded: PartialRecord<string, TotalAmount>,
    extraKmAmount: PartialRecord<string, ExtraKmAmount>
}


export const getTotalToBePaid = async (params: IGetTotalToBePaidParams): Promise<IGetTotalToBePaidReturn> => {
    const response = await axios.post('/booking/instool/total-to-be-paid', params);
    return response.data;
};

export const useTotalToBePaidQuery = (
    params: Partial<IGetTotalToBePaidParams>,
    options?: UseQueryOptions<IGetTotalToBePaidReturn | null>
) => {
    return useQuery<IGetTotalToBePaidReturn | null>({
        queryKey: [ReactQueryKey.TotalToBePaid, {tenant: params.tenant as string, dealer: params.dealer as string}],
        queryFn: () => {
            if (
                !params.pickupDate ||
                !params.dropOffDate ||
                !params.ancillaryDetailIds ||
                !params.promo ||
                !params.rateDetailIds ||
                !params.offerId ||
                !params.tenant || !params.dealer
            ) {
                return null;
            }
            return getTotalToBePaid({
                pickupDate: params.pickupDate,
                dropOffDate: params.dropOffDate,
                ancillaryDetailIds: params.ancillaryDetailIds,
                promo: params.promo,
                rateDetailIds: params.rateDetailIds,
                offerId: params.offerId,
                discountOverpriceSelected: params.discountOverpriceSelected,
                affiliationCode: !!params.affiliationCode ? params.affiliationCode : null,
                tenant: params?.tenant as string,
                dealer: params?.dealer as string,
                language: 'it'
            });
        },
        ...options
    })
};

export const useTotalToBePaidQueryObserver = (params: IMultiTenant) => {
    return useQuery<IGetTotalToBePaidReturn | null>({
        queryKey: [ReactQueryKey.TotalToBePaid, {tenant: params.tenant as string, dealer: params.dealer as string}],
        enabled: false
    })
}

export default getTotalToBePaid;
